import { Fragment, type JSX } from 'react'
import RichText from '@/components/content/common/RichText'
import Section from '@/components/content/common/Section'
import { scrollToSection, toSlug } from '@/lib/utils'
import type { Block, SectionItem } from '@/types/app'

export type GenerateStructureProps = {
  content: any[]
  includeHeading?: boolean
  rootLevel?: number
}

export type GenerateStructureResult = {
  roots: SectionItem[]
  preamble: Block[]
}

export function generateStructure({
  content,
  includeHeading = false,
  rootLevel = 2,
}: GenerateStructureProps): GenerateStructureResult {
  while (rootLevel <= 4) {
    const roots: SectionItem[] = []
    const preamble: Block[] = []
    let currentRoot: SectionItem | null = null
    let currentBranch: SectionItem | null = null
    let currentParentBranch: SectionItem | null = null
    let lastWasAppTemplate = false

    const isAppTemplate = (section: any) =>
      section.type === 'block' && section.fields?.blockType === 'appTemplate'

    const createNodeFromSection = (section: any): SectionItem => {
      const isApp = section.type === 'block' && section.fields?.blockType === 'appTemplate'
      return {
        title: isApp ? section.fields?.app?.name || '' : section.children?.[0]?.text || '',
        content: (includeHeading && section.tag === `h${rootLevel}`) || isApp ? [section] : [],
        app: isApp ? section.fields?.app : null,
        children: [],
        depth: 'root',
      }
    }

    content.forEach((section: any) => {
      // Handle root level (h2)
      if (section.tag === `h${rootLevel}`) {
        if (currentRoot) {
          roots.push(currentRoot)
        }
        currentRoot = createNodeFromSection(section)
        currentRoot.depth = 'root'
        currentBranch = null
        currentParentBranch = null
        lastWasAppTemplate = false
      }
      // Collect content that appears before the first heading
      else if (!currentRoot && !isAppTemplate(section)) {
        preamble.push(section)
      }
      // Handle branch level (h3)
      else if ((section.tag === `h${rootLevel + 1}` || isAppTemplate(section)) && currentRoot) {
        currentBranch = createNodeFromSection(section)
        currentBranch.depth = 'branch'
        currentRoot.children?.push(currentBranch)
        currentParentBranch = currentBranch
        lastWasAppTemplate = isAppTemplate(section)
      }
      // Handle leaf level (h4)
      else if ((section.tag === `h${rootLevel + 2}` || isAppTemplate(section)) && currentBranch) {
        const leaf = createNodeFromSection(section)
        leaf.depth = 'leaf'
        ;(currentParentBranch || currentBranch).children?.push(leaf)
        currentBranch = leaf
        lastWasAppTemplate = isAppTemplate(section)
      }
      // If previous was appTemplate and this is a paragraph (or non-heading), treat as new branch/leaf
      else if (lastWasAppTemplate && section.type === 'paragraph' && currentRoot) {
        // Start a new branch for this paragraph
        const paraNode: SectionItem = {
          title: null,
          content: [section],
          app: null,
          children: [],
          depth: 'branch',
        }
        currentRoot.children?.push(paraNode)
        currentBranch = paraNode
        lastWasAppTemplate = false
      }

      // Add content to the current section
      else if (currentBranch?.content) {
        if (Array.isArray(currentBranch.content)) {
          currentBranch.content.push(section)
        } else {
          console.error('Current branch content is not an array')
        }
        lastWasAppTemplate = false
      } else if (currentRoot?.content) {
        if (Array.isArray(currentRoot.content)) {
          currentRoot.content.push(section)
        } else {
          console.error('Current root content is not an array')
        }
        lastWasAppTemplate = false
      }
    })

    if (currentRoot) {
      roots.push(currentRoot)
    }

    if (roots.length > 0) {
      return { roots, preamble }
    }

    rootLevel++
  }

  return { roots: [], preamble: [] }
}

type RenderContentProps = {
  roots: SectionItem[]
  preamble?: Block[]
  indent?: boolean
  offset?: number
  rootTag?: number
  collapsed?: boolean
  slugPrefix?: string
}

export type RenderContentResult = {
  sections: SectionItem[]
  preamble: React.ReactNode | null
}

export const renderContent = ({
  roots,
  preamble,
  indent = false,
  offset = 154,
  rootTag,
  collapsed = false,
  slugPrefix,
}: RenderContentProps): RenderContentResult => {
  const sections = roots.map((root: SectionItem) => {
    const slug = toSlug(root.title || '')
    const scopedSlug = slugPrefix && slug ? `${slugPrefix}-${slug}` : slug

    const renderNode = (node: SectionItem, level: number = 0): React.ReactNode => {
      const nodeContent =
        Array.isArray(node.content) && node.content.length > 0 ? (
          <RichText
            key={toSlug(node.title || '')}
            id={scopedSlug}
            indent={indent}
            content={(node.content as Block[]) || []}
            offset={offset}
          />
        ) : null

      // Cap nesting at level 2 - beyond that, render content without Section wrappers
      if (level >= 2) {
        const HeadingTag = `h${Math.min((rootTag ?? 2) + level, 6)}` as keyof JSX.IntrinsicElements
        const id = scopedSlug
          ? `${scopedSlug}-${toSlug(node.title || '')}`
          : toSlug(node.title || '')

        return (
          <div key={id}>
            {node.title && (
              <HeadingTag
                id={id}
                data-section-id={id}
                data-name={node.title}
                onClick={() => {
                  scrollToSection({ id, offset, animate: true, hideHash: true })
                }}
                className="underlined mb-2 cursor-pointer"
              >
                {node.title}
              </HeadingTag>
            )}
            {nodeContent}
            {node.children &&
              node.children.map((child: SectionItem, childIndex: number) => (
                <Fragment key={`${toSlug(child.title || '')}-${childIndex}`}>
                  {renderNode(child, level + 1)}
                </Fragment>
              ))}
          </div>
        )
      }

      if (!node.children || node.children.length === 0) {
        return nodeContent
      }

      return (
        <Fragment key={slug}>
          {nodeContent}
          {node.children.map((child: SectionItem, childIndex: number) => {
            if (child.title && !child.app && child.depth !== 'leaf') {
              return (
                <Section
                  key={`${child.title}-${childIndex}`}
                  sectionId={`${scopedSlug}-${toSlug(child.title)}`}
                  title={child.title}
                  isChild
                  offset={offset}
                  depth={child.depth}
                  rootTag={rootTag}
                  collapsed={collapsed}
                >
                  {renderNode(child, level + 1)}
                </Section>
              )
            } else {
              return renderNode(child, level + 1)
            }
          })}
        </Fragment>
      )
    }

    return {
      ...root,
      slug: scopedSlug,
      rootTag,
      content: renderNode(root),
    }
  })

  const preambleNode =
    preamble && preamble.length > 0 ? (
      <RichText content={preamble} indent={indent} offset={offset} />
    ) : null

  return { sections, preamble: preambleNode }
}
